import { ChangeDetectorRef, Component, ElementRef, Inject, Input, OnDestroy, OnInit, Optional, ViewChild, ViewEncapsulation } from "@angular/core";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";

import { TranslateService } from "@ngx-translate/core";

import { models } from "powerbi-client";
import { IPaginatedReportLoadConfiguration, IPaginatedReportParameter } from 'powerbi-models';

import { ReportService } from "@bpm/services";
import { PowerBiService } from "@core/services";

import { CustomSwalService } from "app/helpers";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { AuthenticationService, AuthenticationTypeService } from "@common/services";
import { InsightsService } from "@core/services";
import { environment } from "environments/environment";

@Component({
    selector: "app-report-viewer-modal",
    templateUrl: "./report-viewer-modal.component.html",
    styleUrls: ["./report-viewer-modal.component.scss"],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class ReportViewerModalComponent implements OnInit, OnDestroy {
  // Inputs
  // Subtítulo (estado y nombre del objeto); el título va traducido en la plantilla
  @Input() title: string = "";
  @Input() menuId: number;
  @Input() objectIds: any[];

  // public
  public urlReport: string;
  public langEnglish: boolean;
  public isEmbeded: boolean = false;
  public reportConfig: IPaginatedReportLoadConfiguration;
  public eventHandlers: any;
  private apiKeyPromise: Promise<string>;

  //private
  private _unsubscribeAll: Subject<any>;

  @ViewChild('reportViewer', { static: true }) reportViewer: ElementRef;

  //https://github.com/ramandeep-singh-1983/ngx-powerbi/blob/master/projects/ngx-powerbi/src/lib/ngx-powerbi.service.ts

  constructor(
    public activeModal: NgbActiveModal,
    private _swalService: CustomSwalService,
    private _reportService: ReportService,
    private _traslateService: TranslateService,
    private _powerBIService: PowerBiService,
    private _authenticationTypeService: AuthenticationTypeService,
    private _cdr: ChangeDetectorRef,
    private _insightsService: InsightsService,
    private _authenticationService: AuthenticationService
  ) {
    this._unsubscribeAll = new Subject();
  }

  ngOnInit(): void {

    this.log('Abrió un reporte', this.logData());

    this._swalService.showLoading();

    // Obtenemos la api key; el reporte la espera antes de armar sus parámetros
    this.apiKeyPromise = this._authenticationTypeService.getApiKey();

    //verificamos lenguaje actual
    this.langEnglish = this._traslateService.currentLang == 'en' ? true : false;

    // Ejecuta un evento para esperar hasta obtener el token para power bi
    this._powerBIService.onAccessTokenChanged
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(
        response => {
          if (response) {
            this.loadReport();
          }
        },
        error => {
          this.log('Error al obtener el token de Power BI', this.logData(), error, true);
          this._swalService.close();
          this._traslateService.get(['COMMON.ERROR_INTECEPTOR.POWER_BI_CANNOT_GET_TOKEN']).pipe(takeUntil(this._unsubscribeAll)).subscribe(value => {
            this._swalService.showErrorMsg(value['COMMON.ERROR_INTECEPTOR.POWER_BI_CANNOT_GET_TOKEN']);
          });
        }
      );

    // Inicializa el servicio de power bi para obtener el token
    this._powerBIService.register();

  }

  /**
 * On Destroy
 */
  ngOnDestroy(): void {
    // Unsubscribe from all subscription
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }

  /**
   * Función que sirve para recargar el reporte
   */
  reload(): void {

    this.log('Recargó un reporte', this.logData());

    // Se limpian las variables
    this.isEmbeded = false;
    this.reportConfig = null;
    this.eventHandlers = null;

    // Se inizializa el show loading
    this._swalService.showLoading();

    // Inicializa el servicio de power bi para obtener el token
    this._powerBIService.register();
  }

  /**
   * Función que sirve para cerrar el modal
   */
  closeModal(): void {
    this.log('Cerró un reporte', this.logData());
    this.activeModal.close();
  }

  /**
   * Función que sirve para cargar el reporte
   */
  private loadReport(): void {
    // Obtiene el reporte por medio del id del permiso
    this._reportService.getByMenuId(this.menuId).pipe(takeUntil(this._unsubscribeAll)).subscribe(
      resp => {

        //seteamos el serverReference dependiendo del lenguaje
        const serverReferenceId = !this.langEnglish ? resp.serverReferenceId : resp.serverReferenceIdEnglish;

        // Obtiene el reporte por medio del grupo y id del reporte
        Promise.all([
          this._powerBIService.getReportEmbed(resp.groupId, serverReferenceId),
          this.apiKeyPromise,
        ]).then(([report, apiKey]) => {

          // Crea un listado de valores de parametros
          const paramsValues: IPaginatedReportParameter[] = resp.params.split(",").map(
            (param, index) => {
              // Verifica si viene un parametro con el nombre de apikey
              if (param.toLocaleLowerCase() == 'apikey') {
                return { name: param, value: apiKey };
              }
              return { name: param, value: this.objectIds[index] };
            }
          );

          // Genera una url del reporte para el iframe
          this.urlReport = `${report.embedUrl}&${paramsValues.map(value => `rp:${value.name}=${value.value}`).join("&")}`

          // Genera la configuración del reporte
          this.reportConfig = {
            type: "report",
            accessToken: this._powerBIService.accessToken,
            id: serverReferenceId,
            embedUrl: this.urlReport,
            tokenType: models.TokenType.Aad,
            settings: {
              commands: {
                parameterPanel: {
                  enabled: true,
                  expanded: true
                }
              }
            }
          };

          // 'rendered' se dispara en cada repintado: no se traza
          this.eventHandlers = new Map([
            ['loaded', () => this.log('Cargó un reporte', { ...this.logData(), reportId: resp.id ?? null })],
            ['error', (event) => this.log('Error de Power BI al mostrar el reporte', { ...this.logData(), reportId: resp.id ?? null }, event?.detail, true)]
          ]);

          this.isEmbeded = true;
          this._cdr.detectChanges();

          this._swalService.close();
        }).catch((error) => {
          this.log('Error al embeber el reporte de Power BI', { ...this.logData(), reportId: resp.id ?? null }, error, true);
          this._swalService.close();
          this._traslateService.get(['COMMON.ERROR_INTECEPTOR.POWER_BI_CANNOT_GET_EMBEDDED_REPORT']).pipe(takeUntil(this._unsubscribeAll)).subscribe(value => {
            this._swalService.showErrorMsg(value['COMMON.ERROR_INTECEPTOR.POWER_BI_CANNOT_GET_EMBEDDED_REPORT']);
          });
        });

      },
      error => {
        this.log('Error al obtener la configuración del reporte', this.logData(), error, true);
        this._swalService.close();
        this._traslateService.get(['COMMON.ERROR_INTECEPTOR.POWER_BI_CANNOT_GET_REPORT']).pipe(takeUntil(this._unsubscribeAll)).subscribe(value => {
          this._swalService.showErrorMsg(value['COMMON.ERROR_INTECEPTOR.POWER_BI_CANNOT_GET_REPORT']);
        });
      }
    );
  }

  /**
   * Contexto del reporte para las trazas. Nunca la URL embebida ni la api key.
   */
  private logData(): any {
    return {
      menuId: this.menuId ?? null,
      objectIds: this.objectIds ?? [],
      lang: this.langEnglish ? 'en' : 'es',
    };
  }

  /**
   * Manda una traza a Application Insights, etiquetada con el proyecto y el
   * usuario que la originó.
   */
  private log(message: string, data: any, ex: any = null, error: boolean = false): void {
    const user = this._authenticationService.currentUserValue;

    if (error) {
      this._insightsService.traceError(environment.projects.Reports, message, data, ex, user);
      return;
    }

    this._insightsService.traceInfo(environment.projects.Reports, message, data, user);
  }

}
